import React, { useState } from "react";
import "./recent-designs.scss";
import { Tabs, Tab, Menu, MenuItem, InputBase, Pagination } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useGetDesignsQuery, useDeleteDesignMutation } from "../../services/auth";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const STATUS_TABS = [undefined, 'Draft', 'Saved', 'Claimed'] as const;
const parseDesignsList = (res: any): any[] => {
  const data = res?.data || res;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.designs)) return data.designs;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const getStatusClass = (status: string) => {
  const normalized = (status || '').toLowerCase().replace(' ', '-');
  return `status--${normalized}`;
};

const isIgnorableDeleteError = (err: any) => {
  const status = err?.status || err?.originalStatus || err?.data?.code;
  const message = String(err?.data?.message || err?.message || '');
  return status === 404 || /not found/i.test(message);
};

const getDesignThumbnail = (design: any) => {
  let expUrl = undefined;
  if (typeof design?.export_url === 'object' && design?.export_url) {
    expUrl = design.export_url.png || design.export_url.jpg || design.export_url.svg;
  } else if (typeof design?.export_url === 'string') {
    expUrl = design.export_url;
  }
  const url = design?.preview_url || expUrl;
  if (!url) return '/static/images/design1.png';
  const timestamp = new Date(design?.updated_at || design?.created_at || Date.now()).getTime();
  return `${url}?t=${timestamp}`;
};

const DesignCard = ({ design, deletingDesignId, handleDeleteDesign, navigate }: any) => {
  const created = design.created_at ? new Date(design.created_at) : null;
  return (
    <div className="design_card" onClick={() => navigate(`/dashboard?design_id=${design.design_id}`)} style={{ cursor: 'pointer' }}>
      <div className="card_image">
        <img
          src={getDesignThumbnail(design)}
          alt={`Design ${design.design_id}`}
        />
        {(design.claimed_by || design.certificate_url) && (
          <div className="owned_badge">OWNED</div>
        )}
        <button
          className="action_btn"
          title="Delete design"
          disabled={deletingDesignId === design.design_id}
          onClick={(e) => handleDeleteDesign(e, design.design_id)}
        >
          <DeleteOutlineIcon />
        </button>
      </div>
      <div className="card_content">
        <div className="card_header">
          <span className="design_id">{design.design_id}</span>
          <span className={`status_badge ${getStatusClass(design.status)}`}>{design.status || 'Draft'}</span>
        </div>
        <div className="card_meta">
          <div className="meta_item">
            <CalendarTodayIcon className="meta_icon" /> {created ? created.toLocaleDateString() : '-'}
          </div>
          <div className="meta_item">
            <AccessTimeIcon className="meta_icon" /> {created ? created.toLocaleTimeString() : '-'}
          </div>
        </div>
        <div className="card_footer">
          <span className="resolution">{design.canvas_dimensions || '2048 x 2048'}</span>
          <OpenInNewIcon className="open_icon" />
        </div>
      </div>
    </div>
  );
};

const RecentDesigns = () => {
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const openSort = Boolean(anchorEl);

  const { data: designsRes } = useGetDesignsQuery({
    limit: 10,
    offset: (page - 1) * 10,
    status: STATUS_TABS[tabIndex],
    search: search || undefined,
    sort_by: sortBy,
  });
  const [deleteDesign] = useDeleteDesignMutation();
  const [deletingDesignId, setDeletingDesignId] = useState<string | null>(null);
  const designs = parseDesignsList(designsRes);
  const totalCount = designsRes?.data?.total || 0;
  const statusCounts = designsRes?.data?.status_counts || {};
  const totalPages = Math.ceil(totalCount / 10);

  const handleDeleteDesign = async (e: React.MouseEvent, designId: string) => {
    e.stopPropagation();
    if (!designId) return;
    setDeletingDesignId(designId);
    try {
      await deleteDesign(designId).unwrap();
      toast.success('Design deleted.');
    } catch (err: any) {
      if (isIgnorableDeleteError(err)) {
        toast.success('Design deleted.');
      } else {
        toast.error(err?.data?.message || err?.message || 'Failed to delete design');
      }
    } finally {
      setDeletingDesignId(null);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="recent_designs_page">
      <div className="recent_designs_page__header">
        <div className="header_left">
          <h1>Recent Designs</h1>
          <p>Browse and manage all your previously generated designs.</p>
        </div>

        <div className="header_right">
          <div className="search_box">
            <SearchIcon className="search_icon" />
            <InputBase
              placeholder="Search by Design ID..."
              className="search_input"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            {search && (
              <CloseIcon
                className="clear_icon"
                onClick={() => {
                  setSearch('');
                  setPage(1);
                }}
              />
            )}
          </div>
          <button className="filter_btn" onClick={handleSortClick}>
            <FilterAltIcon /> Filter
          </button>
          <Menu
            anchorEl={anchorEl}
            open={openSort}
            onClose={handleSortClose}
            PaperProps={{ className: 'sort_menu_paper' }}
          >
            <MenuItem onClick={() => { setSortBy('newest'); setPage(1); handleSortClose(); }}>Newest First</MenuItem>
            <MenuItem onClick={() => { setSortBy('oldest'); setPage(1); handleSortClose(); }}>Oldest First</MenuItem>
          </Menu>
        </div>
      </div>

      <div className="recent_designs_page__filters">
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          className="status_tabs"
          TabIndicatorProps={{ style: { display: 'none' } }}>
          {STATUS_TABS.map((status, index) => {
            const label = status ? status : 'All Designs';
            const countKey = status ? status.toLowerCase() : 'all';
            return (
              <Tab
                key={index}
                label={<div className="tab_label">{label} <span className="count_badge">{statusCounts[countKey] || 0}</span></div>}
                disableRipple
              />
            );
          })}
        </Tabs>
      </div>

      <div className="recent_designs_page__grid">
        {designs.map((design) => (
          <DesignCard
            key={design.design_id}
            design={design}
            deletingDesignId={deletingDesignId}
            handleDeleteDesign={handleDeleteDesign}
            navigate={navigate}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination_container">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            shape="rounded"
            variant="outlined"
          />
        </div>
      )}
    </div>
  );
};

export default RecentDesigns;
